import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Circle, Type, Music2 } from 'lucide-react';
import { startMic, releaseMic, listMics, getMicDevice, setMicDevice, activeMicLabel, acquireMic, idleMic } from '../../lib/samuel/mic';
import { CompareBox } from './CompareBox';

const TTS_EXAMPLES = [
  'Put the book on the foot of the bed.',
  'She sells sea shells by the sea shore.',
  'Would you like a cup of tea?',
  'The weather is lovely this morning.',
  'Thank you very much for your help.',
];

const box = 'rounded-2xl border border-cyan-500/20 bg-slate-900/60 p-4 space-y-3';
const head = 'text-[10px] uppercase tracking-[0.2em] text-cyan-400/80 font-bold';

const BT = /airpods|bluetooth|headset|hands-?free|buds|beats|bose|sony wh|jabra/i;

const MicPicker = ({ mics, device, onPick, label, onAsk }) => {
  const bt = BT.test(label);
  return (
    <div className="space-y-1.5">
      {mics.length < 2 && !label && (
        <button onClick={onAsk} data-testid="mic-device-ask" className="text-[11px] text-cyan-300 underline underline-offset-2 hover:text-amber-300">Scegli quale microfono usare</button>
      )}
      {mics.length > 1 && (
        <select value={device} onChange={(e) => onPick(e.target.value)} data-testid="mic-device-select"
          className="w-full rounded-lg bg-slate-950 border border-cyan-500/30 text-cyan-100 text-xs px-2 py-1.5 focus:outline-none focus:border-amber-400">
          <option value="">Microfono predefinito</option>
          {mics.map((m, i) => <option key={m.deviceId} value={m.deviceId}>{m.label || `Microfono ${i + 1}`}</option>)}
        </select>
      )}
      {bt && (
        <p className="text-[11px] leading-snug text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-lg px-2.5 py-1.5" data-testid="mic-bt-warning">
          Stai usando il microfono delle cuffie Bluetooth: mentre è acceso le cuffie passano in modalità “telefonata” e l’audio gracchia. Scegli il microfono del computer qui sopra e continua ad ascoltare in cuffia.
        </p>
      )}
    </div>
  );
};

const MicBox = ({ lab }) => {
  const [live, setLive] = useState(false);
  const [rec, setRec] = useState(false);
  const [opening, setOpening] = useState(false);
  const [level, setLevel] = useState(0);
  const [speech, setSpeech] = useState(false);
  const [mics, setMics] = useState([]);
  const [device, setDevice] = useState(getMicDevice);
  const [label, setLabel] = useState('');
  const micRef = useRef(null);
  const busy = useRef(false);
  const refreshMics = () => { listMics().then(setMics).catch(() => {}); setLabel(activeMicLabel()); };
  useEffect(() => {
    refreshMics();
    navigator.mediaDevices?.addEventListener?.('devicechange', refreshMics);
    return () => { navigator.mediaDevices?.removeEventListener?.('devicechange', refreshMics); micRef.current?.stop(); releaseMic(); };
  }, []);
  useEffect(() => { micRef.current?.pause(!!lab.playing || !!lab.status); }, [lab.playing, lab.status]);

  const stop = () => { micRef.current?.stop(); micRef.current = null; setLive(false); setRec(false); setLevel(0); setSpeech(false); };
  const onUtt = async (wav) => {
    if (busy.current) return;
    busy.current = true;
    await lab.addClip({ wav, kind: 'mic', label: 'Microfono' });
    busy.current = false;
  };
  const open = async (opts, set) => {
    set(true); setOpening(true); lab.eng.resume();
    try { micRef.current = await startMic({ onLevel: setLevel, ...opts }); refreshMics(); }
    catch { set(false); lab.setError('Microfono non disponibile: controlla i permessi del browser'); }
    finally { setOpening(false); }
  };
  const toggleLive = () => (live ? stop() : open({ vad: true, onSpeech: setSpeech, onUtterance: onUtt }, setLive));
  const toggleRec = () => {
    if (!rec) return open({ vad: false }, setRec);
    const m = micRef.current; if (!m) return stop();
    const wavP = m.take(); stop();
    wavP.then((wav) => wav && onUtt(wav));
  };
  const pick = (id) => { stop(); setMicDevice(id); setDevice(id); setLabel(''); };
  const ask = () => acquireMic().then(() => { refreshMics(); idleMic(); }).catch(() => lab.setError('Microfono non disponibile: controlla i permessi del browser'));
  return (
    <div className="space-y-2">
      <p className={head}>Microfono</p>
      <div className="flex gap-2">
        <button onClick={toggleLive} disabled={rec || opening} data-testid="mic-live-toggle" className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-40 ${live ? 'bg-rose-500 text-white' : 'bg-cyan-500 text-slate-950 hover:bg-cyan-400'}`}>
          {live ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />} {live ? (opening ? 'Apro il microfono…' : 'Spegni') : 'Parla, Samuel ripete'}
        </button>
        <button onClick={toggleRec} disabled={live || opening} data-testid="mic-record-toggle" className={`px-4 flex items-center gap-2 rounded-xl text-sm font-bold disabled:opacity-40 ${rec ? 'bg-rose-500 text-white' : 'bg-slate-800 text-cyan-100'}`}>
          <Circle className={`w-3.5 h-3.5 ${rec ? 'fill-white animate-pulse' : 'fill-rose-500 text-rose-500'}`} /> {rec ? 'Stop' : 'Registra'}
        </button>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden" data-testid="mic-level">
        <div className={`h-full transition-[width] duration-75 ${speech ? 'bg-amber-400' : 'bg-cyan-400'}`} style={{ width: `${Math.min(100, level * 900)}%` }} />
      </div>
      <MicPicker mics={mics} device={device} onPick={pick} label={label} onAsk={ask} />
      <p className="text-[10px] text-slate-500">L’audio resta nel tuo browser: viene inviato solo per la trascrizione IPA.</p>
    </div>
  );
};

const ClipsBox = ({ lab, clips }) => (
  <div className="space-y-2">
    <p className={head}>Clip registrate da Steve</p>
    <div className="flex flex-wrap gap-1.5">
      {clips.map((c, i) => (
        <button key={c.url} onClick={() => lab.fromBlob(c.url, { kind: 'clip', label: c.label, text: c.text })} disabled={!!lab.status}
          data-testid={`clip-${i}`} className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-100 text-xs font-semibold hover:bg-cyan-500/20 disabled:opacity-40">
          <Music2 className="w-3 h-3 inline mr-1" />{c.label}
        </button>
      ))}
      {!clips.length && <span className="text-xs text-slate-500">Nessuna clip per questo dialetto.</span>}
    </div>
  </div>
);

const TtsBox = ({ lab }) => {
  const preset = new URLSearchParams(window.location.search).get('text');
  const [text, setText] = useState(() => (preset || TTS_EXAMPLES[Math.floor(Math.random() * TTS_EXAMPLES.length)]).slice(0, 300));
  const ref = useRef(null);
  useEffect(() => { if (preset) setTimeout(() => ref.current?.scrollIntoView({ block: 'center' }), 600); }, [preset]);
  return (
    <div className="space-y-2">
      <p className={head}>Scrivi un testo</p>
      <div className="relative">
        <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} rows={2} data-testid="tts-input"
          className="w-full resize-none rounded-xl bg-slate-950 border border-cyan-500/30 text-white text-sm p-3 pr-24 focus:outline-none focus:border-amber-400" />
        <button onClick={() => text.trim() && lab.fromText(text.trim())} disabled={!text.trim() || !!lab.status} data-testid="tts-say"
          className="absolute right-2 bottom-3 px-3 py-1.5 rounded-lg bg-amber-400 text-slate-950 text-xs font-bold disabled:opacity-40 flex items-center gap-1"><Type className="w-3.5 h-3.5" /> Pronuncia</button>
      </div>
    </div>
  );
};

export const InputPanel = ({ lab, steveClips }) => (
  <div className={box} data-testid="samuel-input-panel">
    <CompareBox lab={lab} />
    <MicBox lab={lab} />
    <ClipsBox lab={lab} clips={steveClips} />
    <TtsBox lab={lab} />
    {lab.error && <p className="text-xs text-rose-300" data-testid="samuel-error">{lab.error}</p>}
  </div>
);
